import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { MdClose, MdSearch, MdCheckCircle, MdCampaign, MdGroups } from 'react-icons/md';
import ReasonModal from '../components/ReasonModal';
import { formatDate, formatINR, statusBadgeClass } from '../utils/format';

const TABS = ['all', 'pending', 'submitted', 'verified', 'rejected'];

export default function Partners() {
  const location = useLocation();
  const [kycTab, setKycTab] = useState('all');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [detail, setDetail] = useState(null); // full partner (+ totals)
  const [rejectFor, setRejectFor] = useState(null);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (kycTab !== 'all') params.kyc = kycTab;
      if (search) params.search = search;
      const res = await api.get('/admin/partners', { params });
      setList(res.data.data || []);
    } catch {
      toast.error('Failed to load partners');
    } finally {
      setLoading(false);
    }
  }, [kycTab, search]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // Read URL search/id parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const searchFromUrl = searchParams.get('search') || searchParams.get('q') || '';
    const idFromUrl = searchParams.get('id');

    if (searchFromUrl || idFromUrl) {
      setKycTab('all');
      if (searchFromUrl) setSearch(searchFromUrl);
      if (idFromUrl) openDetail(idFromUrl);
    }
  }, [location.search]);

  const openDetail = async (id) => {
    setDetail('loading');
    try {
      const res = await api.get(`/admin/partners/${id}`);
      setDetail(res.data.data);
    } catch {
      toast.error('Failed to load partner');
      setDetail(null);
    }
  };

  const act = async (fn) => {
    setBusy(true);
    try {
      await fn();
      setDetail(null);
      setRejectFor(null);
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    } finally { setBusy(false); }
  };

  const verifyKyc = (id) => act(async () => {
    await api.patch(`/admin/partners/${id}/kyc`, { status: 'verified' });
    toast.success('KYC verified');
  });
  const rejectKyc = (reason) => act(async () => {
    await api.patch(`/admin/partners/${rejectFor.id}/kyc`, { status: 'rejected', reason });
    toast.success('KYC rejected');
  });
  const toggleStatus = (p) => act(async () => {
    const next = p.status === 'active' ? 'suspended' : 'active';
    await api.patch(`/admin/partners/${p.id}/status`, { status: next });
    toast.success(`Partner ${next}`);
  });

  return (
    <div className="page">
      <div className="page-header">
        <h2>Partners</h2>
        <button className="btn btn-ghost" onClick={() => setNotifyOpen(true)}>
          <MdCampaign /> Send Notification
        </button>
      </div>

      <div className="tabs">
        {TABS.map((t) => (
          <button key={t} className={`tab ${kycTab === t ? 'active' : ''}`} onClick={() => setKycTab(t)}>
            {t === 'all' ? 'All' : t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      <div className="filter-bar">
        <div className="search-input-wrap">
          <MdSearch className="search-icon" />
          <input className="search-input" placeholder="Search name, phone, code, or wallet balance…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      {loading ? (
        <div className="page-loader">Loading...</div>
      ) : list.length === 0 ? (
        <div className="empty-state"><MdGroups className="empty-icon" /><h3>No partners</h3></div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr><th>Code</th><th>Name</th><th>Phone</th><th>Quality</th><th style={{ textAlign: 'right' }}>Wallet Balance</th><th style={{ textAlign: 'right' }}>Total Earnings</th><th style={{ textAlign: 'right' }}>Pending Payout</th><th>KYC</th><th>Status</th><th>Action</th></tr>
            </thead>
            <tbody>
              {list.map((p) => (
                <tr key={p.id}>
                  <td><strong>{p.partner_code}</strong></td>
                  <td>{p.name || '-'}</td>
                  <td>{p.phone}</td>
                  <td>{p.quality_score}{p.quality_band ? ` (${p.quality_band})` : ''}</td>
                  <td style={{ textAlign: 'right' }}>{formatINR(p.wallet_balance || 0)}</td>
                  <td style={{ textAlign: 'right' }}>{formatINR(p.total_earnings || 0)}</td>
                  <td style={{ textAlign: 'right' }}>{formatINR(p.pending_payout || 0)}</td>
                  <td><span className={`badge ${statusBadgeClass(p.kyc_status)}`}>{p.kyc_status}</span></td>
                  <td><span className={`badge ${statusBadgeClass(p.status)}`}>{p.status}</span></td>
                  <td><button className="btn btn-sm btn-ghost" onClick={() => openDetail(p.id)}>View</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detail && detail !== 'loading' && (
        <div className="modal-overlay" onClick={() => setDetail(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{detail.partner_code} <span className={`badge ${statusBadgeClass(detail.status)}`}>{detail.status}</span></h3>
              <button className="modal-close" onClick={() => setDetail(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <div className="detail-row"><span className="detail-label">Name</span><span>{detail.name || '-'}</span></div>
              <div className="detail-row"><span className="detail-label">Phone</span><span>{detail.phone}</span></div>
              <div className="detail-row"><span className="detail-label">Joined</span><span>{formatDate(detail.created_at)}</span></div>
              <div className="detail-row"><span className="detail-label">Quality</span><span>{detail.quality_score} {detail.quality_band ? `(${detail.quality_band})` : ''}</span></div>
              <div className="detail-row"><span className="detail-label">Installs</span><span>{detail.install_count ?? 0}</span></div>
              <div className="detail-row"><span className="detail-label">Readers</span><span>{detail.reader_count ?? 0}</span></div>
              <div className="detail-row"><span className="detail-label">KYC</span><span><span className={`badge ${statusBadgeClass(detail.kyc_status)}`}>{detail.kyc_status}</span></span></div>

              <div className="card" style={{ marginTop: 12, background: 'var(--bg)' }}>
                <h4 style={{ marginBottom: 10 }}>Payout / KYC</h4>
                <div className="detail-row"><span className="detail-label">UPI</span><span>{detail.upi_id || '-'}</span></div>
                <div className="detail-row"><span className="detail-label">Account</span><span>{detail.bank_account_number || '-'} {detail.bank_ifsc ? `(${detail.bank_ifsc})` : ''}</span></div>
                <div className="detail-row"><span className="detail-label">Aadhaar</span><span>{detail.aadhaar_number || '-'}</span></div>
                <div className="detail-row"><span className="detail-label">PAN</span><span>{detail.pan_number || '-'}</span></div>
              </div>

              {detail.totals && (
                <div className="card" style={{ marginTop: 12, background: 'var(--bg)' }}>
                  <h4 style={{ marginBottom: 10 }}>Wallet</h4>
                  <div className="detail-row"><span className="detail-label">Total earned</span><span>{formatINR(detail.totals.total_earnings)}</span></div>
                  <div className="detail-row"><span className="detail-label">Pending payment</span><span>{formatINR(detail.totals.pending_payment)}</span></div>
                  <div className="detail-row"><span className="detail-label">Available</span><span>{formatINR(detail.totals.available_balance)}</span></div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" disabled={busy} onClick={() => toggleStatus(detail)}>
                {detail.status === 'active' ? 'Suspend' : 'Activate'}
              </button>
              {detail.kyc_status !== 'verified' && (
                <>
                  <button className="btn btn-danger" disabled={busy} onClick={() => setRejectFor(detail)}>Reject KYC</button>
                  <button className="btn btn-primary" disabled={busy} onClick={() => verifyKyc(detail.id)}>
                    <MdCheckCircle /> Verify KYC
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {rejectFor && (
        <ReasonModal
          title="Reject KYC"
          reasons={['Document mismatch', 'Unclear / invalid documents', 'Suspicious information', 'Other']}
          confirmLabel="Reject KYC"
          busy={busy}
          onCancel={() => setRejectFor(null)}
          onConfirm={rejectKyc}
        />
      )}

      {notifyOpen && <NotifyModal onClose={() => setNotifyOpen(false)} />}
    </div>
  );
}

function NotifyModal({ onClose }) {
  const [form, setForm] = useState({ title: '', body: '' });
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!form.title.trim()) return toast.error('Title required');
    setBusy(true);
    try {
      await api.post('/admin/partner-notifications', form); // partner_id omitted = broadcast
      toast.success('Notification sent to all partners');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    } finally { setBusy(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-sm" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header"><h3>Broadcast Notification</h3><button className="modal-close" onClick={onClose}><MdClose /></button></div>
        <div className="modal-body">
          <div className="form-group"><label>Title</label><input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} /></div>
          <div className="form-group"><label>Message</label><textarea rows={3} value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} /></div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" onClick={send} disabled={busy}>{busy ? 'Sending…' : 'Send to All'}</button>
        </div>
      </div>
    </div>
  );
}
